/**
 * SPIN TENET — CFD Setup & Study Contracts (Gate 1 Foundation)
 *
 * Formal contracts for:
 * - Prescribed wind conditions
 * - Computational domain extents
 * - Physical boundary condition classifications
 * - Study and Scenario hierarchies
 *
 * ARCHITECTURAL CONTRACT:
 * - Pure durable metadata; zero vertex buffers, zero solver parameters.
 * - Engine independent: zero OpenFOAM, OpenLB, PALM, or waLBerla syntax.
 */

export type BoundaryConditionType =
  | 'velocity_inlet'   // Velocity boundary
  | 'pressure_outlet'  // Pressure / outflow boundary
  | 'no_slip_wall'     // No-slip wall
  | 'slip_wall'        // Slip wall / free-slip
  | 'symmetry'         // Symmetry plane
  | 'open';            // Open / atmospheric boundary

export interface WindSetup {
  /**
   * Prescribed incoming wind speed in meters per second (m/s).
   * Explicitly null when unconfigured / unset. Must be entered by user.
   */
  speed: number | null;

  /**
   * Prescribed incoming wind direction in degrees [0, 360).
   * Explicitly null when unconfigured / unset. Must be entered by user.
   * Convention: 0° = +X axis, positive rotation counter-clockwise towards +Z axis around vertical +Y axis.
   */
  direction: number | null;

  /**
   * Reference coordinate convention documentation string.
   */
  convention: string;
}

export interface DomainSetup {
  /**
   * Computational domain width along local X axis in meters (> 0).
   */
  width: number;

  /**
   * Computational domain length along local Z axis in meters (> 0).
   */
  length: number;

  /**
   * Computational domain height along local Y (vertical) axis in meters (> 0).
   */
  height: number;
}

export interface BoundaryConditionsSetup {
  inlet: BoundaryConditionType;
  outlet: BoundaryConditionType;
  ground: BoundaryConditionType;
  buildings: BoundaryConditionType;
  sides: BoundaryConditionType;
  top: BoundaryConditionType;
}

export interface CfdSetup {
  wind: WindSetup;
  domain: DomainSetup;
  boundaryConditions: BoundaryConditionsSetup;
}

export interface Scenario {
  id: string;
  name: string;
  description?: string;
  isBaseline: boolean;
  cfdSetup: CfdSetup;
}

export interface Study {
  id: string;
  name: string;
  description: string;
  scenarios: Record<string, Scenario>;
  activeScenarioId: string;
}

export const DEFAULT_WIND_CONVENTION =
  'Direction reference: 0° = +X axis. Positive angle rotates counter-clockwise toward +Z axis around vertical +Y axis in local Cartesian coordinates. Geographic North/East alignment deferred until project CRS is defined.';
